import React from 'react';
import { recapPalette, withAlpha } from '@/lib/shareTheme';

/**
 * Square (1080×1080) social-share graphic used by both the basketball
 * `/stats/summary` and the sport `/stats/match` pages. Renders a
 * scoreboard + top-3 performer chips per team + winner banner.
 *
 * Rendered at a fixed 1080×1080 so `html-to-image` captures a consistent
 * PNG regardless of viewport. Callers mount it off-screen and run
 * `preloadImagesForCapture(node)` before `toPng(...)`.
 *
 * Props:
 *   innerRef             — forwarded to the outer capture <div>
 *   teamA / teamB        — { name }
 *   scoreA / scoreB      — string | number
 *   winnerKey            — 'a' | 'b' | null
 *   winnerLabel          — "Team wins by N" (already localised by caller)
 *   headerLabel          — top-left uppercase label (league name)
 *   dateLabel            — top-right formatted date
 *   sportBadge           — small chip next to header ("Soccer") or null
 *   subheading           — under FINAL RECAP ("Team A vs Team B"). Null hides.
 *   setSummary           — "25-22, 21-25, 15-12" / cricket overs line or null
 *   teamAPerformers      — [{ id, jersey, name, primary, secondary }]  (up to 3)
 *   teamBPerformers      — same shape
 *   eventLogoUrl / teamALogoUrl / teamBLogoUrl — plain URLs or ''
 *   theme                — league share_theme { primary, accent } or null (default navy / amber)
 */
export function RecapSquareCard({
  innerRef,
  teamA, teamB,
  scoreA, scoreB,
  winnerKey, winnerLabel,
  headerLabel, dateLabel, sportBadge,
  subheading, setSummary,
  teamAPerformers = [], teamBPerformers = [],
  eventLogoUrl = '', teamALogoUrl = '', teamBLogoUrl = '',
  theme = null,
}) {
  const pal = recapPalette(theme);
  const chip = pal.light ? 'bg-black/5' : 'bg-white/10';

  const TeamBlock = ({ team, score, logoUrl, isWinner, performers }) => (
    <div className="flex-1 flex flex-col items-center text-center px-2 min-w-0">
      <div
        className={`w-[220px] h-[220px] rounded-full flex items-center justify-center overflow-hidden mb-4 ${chip}`}
        style={{ boxShadow: isWinner ? `0 0 0 4px ${pal.accent}, 0 0 40px ${withAlpha(pal.accent, 0.5)}` : `0 0 0 2px ${withAlpha(pal.ink, 0.25)}` }}
      >
        {logoUrl ? (
          <img src={logoUrl} alt={`${team.name} logo`} crossOrigin="anonymous" className="w-full h-full object-contain" />
        ) : (
          <div className="opacity-80 text-7xl font-black">{(team.name || '').slice(0, 1).toUpperCase()}</div>
        )}
      </div>
      <div className="text-lg uppercase tracking-widest opacity-70 font-bold truncate max-w-full mb-1 px-2">{team.name}</div>
      <div
        className="font-black tabular-nums leading-none drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]"
        style={{ fontSize: 132, color: isWinner ? pal.accent : pal.ink }}
        data-testid={`recap-score-${team === teamA ? 'a' : 'b'}`}
      >
        {score ?? '—'}
      </div>
      {performers.length > 0 && (
        <div className="mt-6 w-full space-y-2 max-w-[380px]">
          {performers.slice(0, 3).map((p) => (
            <div key={p.id} className="flex items-center gap-2 text-left">
              <div className="italic font-black w-9 text-right shrink-0" style={{ fontSize: 20, color: pal.accent }}>#{p.jersey || '?'}</div>
              <div className="flex-1 min-w-0 truncate font-semibold" style={{ fontSize: 20 }}>{p.name}</div>
              <div className="font-black tabular-nums shrink-0" style={{ fontSize: 22 }}>{p.primary}</div>
              {p.secondary && (
                <div className="opacity-60 tabular-nums w-16 text-right shrink-0" style={{ fontSize: 15 }}>{p.secondary}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div
      ref={innerRef}
      data-testid="recap-card"
      className="relative"
      style={{ width: 1080, height: 1080, padding: 56, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box', background: pal.background, color: pal.ink }}
    >
      {/* subtle accent glow */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{ backgroundImage: pal.glow }} />

      {/* HEADER STRIP */}
      <div className="relative flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          {eventLogoUrl ? (
            <img src={eventLogoUrl} alt="" crossOrigin="anonymous" className={`w-16 h-16 rounded-xl object-contain p-1 ${chip}`} data-testid="recap-event-logo" />
          ) : null}
          <div className="text-[13px] font-black uppercase tracking-[0.35em] max-w-[560px] leading-snug">
            <span className="opacity-80">{(headerLabel || '').toUpperCase()}</span>
            {sportBadge ? (
              <span className="ml-3 inline-block px-2 py-0.5 rounded" style={{ fontSize: 10, color: pal.accent, backgroundColor: withAlpha(pal.accent, 0.2) }}>
                {sportBadge}
              </span>
            ) : null}
          </div>
        </div>
        <div className="text-right opacity-60 uppercase tracking-widest font-semibold" style={{ fontSize: 14 }}>{dateLabel || ''}</div>
      </div>

      {/* TITLE */}
      <div className="relative text-center mb-2">
        <div className="font-black uppercase leading-none tracking-tight" style={{ fontSize: 116, letterSpacing: '-0.02em' }}>
          <span>FINAL </span>
          <span style={{ color: pal.accent }} data-testid="recap-title-accent">RECAP</span>
        </div>
      </div>

      {/* MATCHUP LINE */}
      {subheading && (
        <div className="relative text-center opacity-90 font-semibold mb-6" style={{ fontSize: 24 }}>{subheading}</div>
      )}

      {/* SCOREBOARD + PERFORMERS */}
      <div className="relative flex items-stretch justify-between gap-3">
        <TeamBlock team={teamA} score={scoreA} logoUrl={teamALogoUrl} isWinner={winnerKey === 'a'} performers={teamAPerformers} />
        <div className="w-px mx-1" style={{ backgroundColor: withAlpha(pal.ink, 0.1) }} />
        <TeamBlock team={teamB} score={scoreB} logoUrl={teamBLogoUrl} isWinner={winnerKey === 'b'} performers={teamBPerformers} />
      </div>

      {/* SET SUMMARY (volleyball / pickleball) or cricket overs */}
      {setSummary && (
        <div className="relative mt-4 text-center font-mono tracking-wider" style={{ fontSize: 16, color: pal.accent }} data-testid="recap-set-summary">
          {setSummary}
        </div>
      )}

      {/* WINNER BANNER */}
      {winnerLabel && (
        <div className="relative mt-6 flex justify-center">
          <span className="inline-block font-black uppercase tracking-widest px-5 py-2 rounded-full" style={{ fontSize: 18, ...pal.banner }} data-testid="recap-winner-banner">
            {winnerLabel}
          </span>
        </div>
      )}

      {/* FOOTER */}
      <div className="absolute left-0 right-0 bottom-6 text-center opacity-30 uppercase tracking-[0.45em] font-semibold" style={{ fontSize: 12 }}>
        via Schedule Guru · scheduleguru.ws
      </div>
    </div>
  );
}
